/**
 * Type-to-filter app picker. Unlike UserCombobox (which hits `/api/users`
 * per keystroke), the candidate apps are already loaded by the caller — this
 * just filters that list client-side, so it scales to a large workspace
 * without a plain `<select>` becoming unusable.
 */

import { useEffect, useRef, useState } from 'react';
import type { AppSummary } from '../api/types.js';
import { inputClass } from './ui.js';

interface AppComboboxProps {
  apps: AppSummary[];
  value: string;
  onChange: (slug: string) => void;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  id?: string;
  'aria-label'?: string;
}

const MAX_RESULTS = 50;

function label(app: AppSummary): string {
  return `${app.name} (${app.slug})`;
}

export function AppCombobox({
  apps,
  value,
  onChange,
  placeholder,
  required,
  disabled,
  id,
  'aria-label': ariaLabel,
}: AppComboboxProps) {
  const selected = apps.find((a) => a.slug === value);
  const [query, setQuery] = useState(selected ? label(selected) : '');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const ref = useRef<HTMLDivElement>(null);

  // Keep the input text in sync when the selection changes from outside
  // (e.g. the form resets `value` to '' after a successful submit).
  useEffect(() => {
    const app = apps.find((a) => a.slug === value);
    setQuery(app ? label(app) : '');
  }, [value, apps]);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const q = value ? '' : query.trim().toLowerCase();
  const results = (
    q === ''
      ? apps
      : apps.filter((a) => a.name.toLowerCase().includes(q) || a.slug.toLowerCase().includes(q))
  )
    .slice()
    .sort((a, b) => a.name.localeCompare(b.name))
    .slice(0, MAX_RESULTS);

  function pick(app: AppSummary) {
    onChange(app.slug);
    setQuery(label(app));
    setOpen(false);
    setHighlight(-1);
  }

  const showDropdown = open;

  return (
    <div className="relative" ref={ref}>
      <input
        id={id}
        aria-label={ariaLabel}
        type="text"
        required={required}
        disabled={disabled}
        value={query}
        placeholder={placeholder}
        autoComplete="off"
        onChange={(e) => {
          setQuery(e.target.value);
          if (value) onChange('');
          setOpen(true);
          setHighlight(-1);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (!showDropdown) return;
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlight((h) => Math.min(h + 1, results.length - 1));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlight((h) => Math.max(h - 1, 0));
          } else if (e.key === 'Enter' && highlight >= 0 && results[highlight]) {
            e.preventDefault();
            pick(results[highlight]);
          } else if (e.key === 'Escape') {
            setOpen(false);
          }
        }}
        className={inputClass}
      />
      {showDropdown && (
        <ul className="absolute z-20 mt-1.5 w-full max-h-56 overflow-auto rounded-lg border border-line bg-surface shadow-elevated animate-fade-in">
          {results.length === 0 ? (
            <li className="px-3 py-2 text-sm text-muted">No matching apps</li>
          ) : (
            results.map((a, i) => (
              <li key={a.slug}>
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pick(a)}
                  className={`w-full text-left px-3 py-2 text-sm transition-colors flex items-center gap-2 ${
                    i === highlight ? 'bg-brand-soft text-ink' : 'hover:bg-surface-2 text-ink'
                  }`}
                >
                  <span className="font-medium min-w-0 truncate">{a.name}</span>
                  <span className="text-muted min-w-0 truncate ml-auto">{a.slug}</span>
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
